import React, { useCallback, useState } from "react";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import { SudoButton } from "../sudokit/components/SudoButton";
import { Text } from "../sudokit/components/Text";
import HStack from "../sudokit/Layout/HStack";
import VStack from "../sudokit/Layout/VStack";
import type { PluginDefinition, RepairChangeLog } from "../utils/repairPluginDocumentVariables";
import { repairPluginDocumentVariables, summarizeChangeLog } from "../utils/repairPluginDocumentVariables";

interface PluginResponse {
  plugin: PluginDefinition | null;
}

interface PluginSearchResponse {
  plugins: PluginDefinition[];
}

interface RepairStateEntry {
  loading: boolean;
  error: boolean;
  message: string | null;
  changeLog: RepairChangeLog | null;
}

const deduplicateById = (plugins: PluginDefinition[]): PluginDefinition[] => {
  const seen = new Set<string>();
  const deduped: PluginDefinition[] = [];

  plugins.forEach((plugin) => {
    if (!plugin || seen.has(plugin.id)) {
      return;
    }
    seen.add(plugin.id);
    deduped.push(plugin);
  });

  return deduped;
};

const initialRepairEntry: RepairStateEntry = {
  loading: false,
  error: false,
  message: null,
  changeLog: null,
};

export default function PluginRepair(): JSX.Element {
  const [query, setQuery] = useState<string>("");
  const [searching, setSearching] = useState<boolean>(false);
  const [results, setResults] = useState<PluginDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [repairState, setRepairState] = useState<Record<string, RepairStateEntry>>({});
  const { setToast } = useToast();

  const handleSearch = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = query.trim();
      if (!trimmed) {
        setError("Enter a plugin name or ID to search.");
        setResults([]);
        return;
      }

      setSearching(true);
      setError(null);

      try {
        const matches: PluginDefinition[] = [];

        // Attempt direct lookup by ID / shortId first
        try {
          const response = (await Auth.fetchWithIdToken(`/api/plugins/${trimmed}`, { method: "POST" })) as PluginResponse;
          if (response?.plugin) {
            matches.push(response.plugin);
          }
        } catch (err: unknown) {
          const status = (err as { status?: number } | undefined)?.status;
          if (status && status !== 404) {
            // ignore errors other than 404; we'll fall back to search
          }
        }

        // Fall back to fuzzy search if needed
        if (matches.length === 0) {
          try {
            const response = (await Auth.fetchWithIdToken("/api/plugins/search", { query: trimmed })) as PluginSearchResponse;
            if (Array.isArray(response?.plugins)) {
              matches.push(...response.plugins);
            }
          } catch (searchErr) {
            throw searchErr;
          }
        }

        const deduped = deduplicateById(matches);

        if (deduped.length === 0) {
          setError("No plugins found for that query.");
          setResults([]);
          return;
        }

        const enriched = await Promise.all(
          deduped.map(async (plugin) => {
            try {
              const detailResponse = (await Auth.fetchWithIdToken(`/api/plugins/${plugin.id}`, { method: "POST" })) as PluginResponse;
              return detailResponse?.plugin ?? plugin;
            } catch (detailErr) {
              return plugin;
            }
          })
        );

        setResults(enriched);
      } catch (err) {
        setError((err as Error)?.message || "Search failed. Please try again.");
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [query]
  );

  const handleRepair = useCallback(
    async (pluginId: string) => {
      setRepairState((prev) => ({
        ...prev,
        [pluginId]: {
          ...initialRepairEntry,
          loading: true,
        },
      }));

      try {
        const response = (await Auth.fetchWithIdToken(`/api/plugins/${pluginId}`, { method: "POST" })) as PluginResponse;
        const plugin = response?.plugin;
        if (!plugin) {
          throw new Error("Plugin not found");
        }

        const { updated, updatedPlugin, changeLog } = repairPluginDocumentVariables(plugin);

        if (!updated) {
          setRepairState((prev) => ({
            ...prev,
            [pluginId]: {
              loading: false,
              error: false,
              message: summarizeChangeLog(changeLog),
              changeLog,
            },
          }));
          return;
        }

        await Auth.fetchWithIdToken(`/api/plugins/${pluginId}`, {
          method: "PUT",
          name: updatedPlugin.name,
          type: updatedPlugin.type,
          description: updatedPlugin.description,
          blocks: updatedPlugin.blocks,
          connections: updatedPlugin.connections,
          status: updatedPlugin.status,
          categoryId: updatedPlugin.categoryId,
          promptVisibility: updatedPlugin.promptVisibility,
        });

        setResults((prev) => prev.map((existing) => (existing.id === pluginId ? { ...existing, ...updatedPlugin } : existing)));

        setRepairState((prev) => ({
          ...prev,
          [pluginId]: {
            loading: false,
            error: false,
            message: summarizeChangeLog(changeLog),
            changeLog,
          },
        }));

        setToast(`Repaired ${updatedPlugin.name}`);
      } catch (err) {
        const message = (err as Error)?.message || "Repair failed. Please try again.";
        setRepairState((prev) => ({
          ...prev,
          [pluginId]: {
            loading: false,
            error: true,
            message,
            changeLog: null,
          },
        }));
        setToast(message);
      }
    },
    [setToast]
  );

  const renderResult = (plugin: PluginDefinition) => {
    const state = repairState[plugin.id] || initialRepairEntry;

    const status = plugin.status ?? "UNKNOWN";
    const statusClasses =
      status === "PUBLISHED" ? "bg-emerald-500/20 text-emerald-800 border" : status === "UNLISTED" ? "bg-amber-500/20 text-amber-800 border " : "bg-slate-300/40 text-slate-800 border";

    return (
      <div
        key={plugin.id}
        className="rounded-xl border  bg-slate-50 p-4"
      >
        <HStack
          className="items-start justify-between gap-3"
          wrap
        >
          <VStack spacing={2}>
            <div className="flex items-center gap-2">
              <Text className="text-lg font-semibold text-slate-500">{plugin.name}</Text>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusClasses}`}>{status}</span>
            </div>
            <div className="grid gap-y-1 gap-x-6 text-md leading-relaxed text-slate-300 sm:grid-cols-2">
              <div>
                <span className="font-semibold text-slate-500">Plugin ID:</span>
                <span className="ml-2 select-all font-mono text-slate-600">{plugin.id}</span>
              </div>
              <div>
                <span className="font-semibold text-slate-500">Short ID:</span>
                <span className="ml-2 font-mono text-slate-600">{plugin.shortId || "–"}</span>
              </div>
              <div>
                <span className="font-semibold text-slate-500">Owner ID:</span>
                <span className="ml-2 font-mono text-slate-600">{plugin.userId ?? "–"}</span>
              </div>
              <div>
                <span className="font-semibold text-slate-500">Owner Email:</span>
                <span className="ml-2 font-mono text-slate-600">{plugin.userEmail ?? "–"}</span>
              </div>
              <div>
                <span className="font-semibold text-slate-500">Owner Name:</span>
                <span className="ml-2 text-slate-600">{plugin.userDisplayName ?? "–"}</span>
              </div>
            </div>
          </VStack>
          <SudoButton
            variant="secondary"
            onClick={() => handleRepair(plugin.id)}
            disabled={state.loading}
          >
            {state.loading ? "Repairing…" : "Repair"}
          </SudoButton>
        </HStack>
        {state.message && <Text className={`mt-3 text-sm ${state.error ? "text-red-400" : "text-purple-600"}`}>{state.message}</Text>}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Text className="text-2xl font-semibold text-app-grey-0">Plugin Repair</Text>
        <Text className="text-sm text-app-grey-1 mt-1">
          Search for a plugin by name, short ID, or database ID. When you run a repair, we ensure the document inputs (highlighted text, preceding text, previous document text, and documentId) are
          correctly wired between the input block and inference blocks.
        </Text>
      </div>

      <form
        className="flex items-center gap-3"
        onSubmit={handleSearch}
      >
        <input
          className="flex-1 bg-app-document-background border border-app-divider rounded px-3 py-2 text-sm text-app-grey-0 focus:outline-none focus:ring-2 focus:ring-app-accent"
          placeholder="Enter plugin name or ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <SudoButton
          type="submit"
          disabled={searching}
        >
          {searching ? "Searching…" : "Search"}
        </SudoButton>
      </form>

      {error && <Text className="text-sm text-red-400">{error}</Text>}

      {!searching && !error && results.length === 0 && <Text className="text-sm text-app-grey-1">No results yet. Start by searching for a plugin.</Text>}

      <VStack spacing={4}>{results.map(renderResult)}</VStack>
    </div>
  );
}
